/**
 * QuizParticipantContext - Unified Context for Quiz Participation
 * 
 * Combines all quiz participant hooks into a single context provider.
 * This is the main entry point for quiz participation state management.
 * 
 * Usage:
 * <QuizParticipantProvider sessionId="abc123">
 *   <QuizScreen />
 * </QuizParticipantProvider>
 * 
 * const { session, timer, answers, jokers, participant } = useQuizParticipant();
 */

import React, { createContext, useContext, useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useParticipantStorage } from './hooks/useParticipantStorage';
import { useQuizSession } from './hooks/useQuizSession';
import { useQuizTimer } from './hooks/useQuizTimer';
import { useQuizAnswers } from './hooks/useQuizAnswers';
import { useJokers } from './hooks/useJokers';
import { Logger } from '../../utils/logger';

const log = Logger.create('QuizParticipantContext');

// Create the context
const QuizParticipantContext = createContext(null);

/**
 * Provider component that wraps quiz participation screens
 */
export function QuizParticipantProvider({ 
  children, 
  sessionIdOrShortCode,
  studentId = null,
  onError = null,
}) {
  // Participant state
  const [participantId, setParticipantId] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const [bonusTime, setBonusTime] = useState(0);

  // Initialize session hook first to get sessionId
  const session = useQuizSession(sessionIdOrShortCode, onError);

  // Storage hook for persistence
  const storage = useParticipantStorage(session.sessionId);

  // Timer hook - pass timerState and serverTimeOffset
  // Note: Not passing submitted to avoid circular dependency with answers hook
  const timer = useQuizTimer(
    session.timerState,
    session.serverTimeOffset
  );

  // Jokers hook with time extension callback
  const handleTimeExtension = useCallback((seconds) => {
    setBonusTime(prev => prev + seconds);
  }, []);

  const jokers = useJokers(
    session.instancePath, // Use full path for Firebase writes
    participantId,
    session.currentQuestion,
    session.currentQuestionIndex,
    session.settings,
    session.participants,
    handleTimeExtension
  );

  // Answers hook - pass hiddenOptions from jokers
  const answers = useQuizAnswers(
    session.instancePath, // Use full path for Firebase writes
    participantId,
    session.currentQuestion,
    session.currentQuestionIndex,
    session.settings,
    timer.timerExpired,
    session.showCorrectAnswer, // Pass showCorrectAnswer for point reveal timing (not showResults!)
    studentId,
    jokers.hiddenOptions // Pass hidden options from 50:50 joker
  );

  // Restore participant from storage on mount (ONLY ONCE)
  const restoredRef = useRef(false);
  useEffect(() => {
    // Only restore once on initial mount
    if (restoredRef.current) return;
    
    if (session.sessionId && storage.hasStoredParticipant && storage.storedData) {
      restoredRef.current = true;
      const stored = storage.storedData;
      setParticipantId(stored.participantId);
      setParticipantName(stored.participantName);
      setIsRegistered(true);
      
      // Restore score, streak and maxStreak
      if (answers.initializeScore) {
        answers.initializeScore(stored.totalScore || 0, stored.currentStreak || 0, stored.maxStreak || 0);
      }
      
      // Restore used jokers
      if (stored.jokersUsed && jokers.initializeJokers) {
        jokers.initializeJokers(stored.jokersUsed);
      }
    }
  }, [session.sessionId, storage.hasStoredParticipant, storage.storedData]);

  // Reset joker effects when question changes
  useEffect(() => {
    jokers.resetQuestionEffects();
    setBonusTime(0); // Reset bonus time per question
  }, [session.currentQuestionIndex]);

  // Persist score updates
  useEffect(() => {
    if (participantId && answers.totalScore > 0) {
      storage.updateScore(answers.totalScore, answers.currentStreak, answers.maxStreak);
    }
  }, [answers.totalScore, answers.currentStreak, answers.maxStreak, participantId, storage]);

  // Persist joker usage
  useEffect(() => {
    if (participantId) {
      const usedJokers = {};
      Object.keys(jokers.availableJokers).forEach(type => {
        if (!jokers.availableJokers[type]) {
          usedJokers[type] = true;
        }
      });
      if (Object.keys(usedJokers).length > 0) {
        storage.updateJokers(usedJokers);
      }
    }
  }, [jokers.availableJokers, participantId]);

  // Record student stats when quiz ends
  useEffect(() => {
    if (
      session.quizStatus === 'completed' && 
      studentId && 
      session.templateId && 
      session.questions?.length > 0 &&
      !answers.statsRecorded
    ) {
      log.debug('Quiz ended - recording student stats');
      
      // Extract teacherUserId from instancePath (format: quiz_instances/{teacherUserId}/{instanceId})
      let teacherUserId = null;
      if (session.instancePath) {
        const pathParts = session.instancePath.split('/');
        if (pathParts.length >= 2) {
          teacherUserId = pathParts[1]; // quiz_instances/[teacherUserId]/instanceId
        }
      }
      
      answers.recordStats(
        session.templateId,
        session.templateName || session.settings?.quizName || 'Quiz',
        session.questions.length,
        session.settings?.courseId || null,
        session.settings?.courseName || null,
        teacherUserId
      );
    }
  }, [
    session.quizStatus, 
    studentId, 
    session.templateId, 
    session.templateName,
    session.questions, 
    session.instancePath,
    session.settings,
    answers.statsRecorded
  ]);

  /**
   * Register a new participant
  /**
   * Register as a participant
   */
  const registerParticipant = useCallback((id, name) => {
    setParticipantId(id);
    setParticipantName(name);
    setIsRegistered(true);
    storage.saveParticipant({ participantId: id, participantName: name });
  }, [storage]);

  /**
   * Clear participant data (leave quiz)
   */
  const leaveQuiz = useCallback(() => {
    setParticipantId(null);
    setParticipantName('');
    setIsRegistered(false);
    storage.clearParticipant();
  }, [storage]);

  // Combined context value
  const contextValue = {
    // Session state (from useQuizSession)
    session: {
      sessionId: session.sessionId,
      shortCode: session.shortCode,
      instancePath: session.instancePath, // Full Firebase path for writes
      questions: session.questions || [],
      currentQuestion: session.currentQuestion,
      currentQuestionIndex: session.currentQuestionIndex,
      totalQuestions: (session.questions?.length) || 0,
      settings: session.settings || {},
      showCorrectAnswer: session.showCorrectAnswer,
      answersLocked: session.answersLocked,
      participants: session.participants || [],
      isLoading: session.isLoading,
      error: session.error,
      templateId: session.templateId,
      templateName: session.templateName,
      quizStatus: session.quizStatus, // 'active', 'completed', etc.
    },

    // Timer state (from useQuizTimer)
    timer: {
      timeRemaining: timer.timeRemaining,
      timerExpired: timer.timerExpired,
      timerActive: timer.timerActive,
      hasTimer: timer.hasTimer,
      isPaused: timer.isPaused,
      getTimerColorClass: timer.getTimerColorClass,
      getTimerTextColorClass: timer.getTimerTextColorClass,
      getUrgencyLevel: timer.getUrgencyLevel,
      bonusTime,
    },

    // Answer state (from useQuizAnswers)
    answers: {
      selectedOptions: answers.selectedOptions,
      handleSelectOption: answers.handleSelectOption,
      setSelectedOptions: answers.setSelectedOptions,
      submitted: answers.submitted,
      submitAnswer: answers.submitAnswer,
      totalScore: answers.totalScore,
      lastPointsEarned: answers.lastPointsEarned,
      pointsAwarded: answers.pointsAwarded,
      currentStreak: answers.currentStreak,
      maxStreak: answers.maxStreak,
      checkAnswer: answers.checkAnswer,
      recordStats: answers.recordStats,
      statsRecorded: answers.statsRecorded,
      answersMap: answers.answersMap, // Track all answers for final stats
    },

    // Joker state (from useJokers)
    jokers: {
      availableJokers: jokers.availableJokers,
      isJokerAvailable: jokers.isJokerAvailable,
      useFiftyFiftyJoker: jokers.useFiftyFiftyJoker,
      useAudienceJoker: jokers.useAudienceJoker,
      useBestPlayerJoker: jokers.useBestPlayerJoker,
      useTimeExtensionJoker: jokers.useTimeExtensionJoker,
      hiddenOptions: jokers.hiddenOptions,
      isOptionHidden: jokers.isOptionHidden,
      audienceResults: jokers.audienceResults,
      getAudienceVote: jokers.getAudienceVote,
      bestPlayerAnswer: jokers.bestPlayerAnswer,
      isBestPlayerChoice: jokers.isBestPlayerChoice,
      jokerLoading: jokers.jokerLoading,
      JOKER_TYPES: jokers.JOKER_TYPES,
    },

    // Participant state
    participant: {
      participantId,
      participantName,
      isRegistered,
      registerParticipant,
      leaveQuiz,
      studentId,
    },
  };

  return (
    <QuizParticipantContext.Provider value={contextValue}>
      {children}
    </QuizParticipantContext.Provider>
  );
}

/**
 * Hook to access quiz participant context
 */
export function useQuizParticipant() {
  const context = useContext(QuizParticipantContext);
  
  if (!context) {
    throw new Error('useQuizParticipant must be used within a QuizParticipantProvider');
  }
  
  return context;
}

// Export the context itself for advanced use cases
export { QuizParticipantContext };

// Export individual hooks for advanced usage
export { useParticipantStorage } from './hooks/useParticipantStorage';
export { useQuizSession } from './hooks/useQuizSession';
export { useQuizTimer } from './hooks/useQuizTimer';
export { useQuizAnswers } from './hooks/useQuizAnswers';
export { useJokers, JOKER_TYPES, DEFAULT_JOKERS } from './hooks/useJokers';

export default QuizParticipantContext;
